import {
	DEFAULT_SITE_UNITS,
	type DistanceUnit,
	distanceUnits,
} from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import {
	type SpreadingCompactionUnitConfig,
	spreadingCompactionDisplayUnits,
} from "./unitConfig.types";

const MM_PER_METER = 1000;
const CM_PER_METER = 100;
const MM_PER_CENTIMETER = 10;

/**
 * mm granularity. The DB column and the external binary both take Float mm and
 * the binary's output directory name carries 3 decimals, so 1/1000 mm is the
 * finest value that survives a round trip. Rounding here also drops the float
 * residue a bare `display * factor` leaves (30.29 cm -> 302.90000000000003 mm).
 */
const MM_DECIMALS = 3;

/** Snaps a mm value to 1/1000 mm. */
function roundMm(mm: number): number {
	const base = 10 ** MM_DECIMALS;
	return Math.round(mm * base) / base;
}

function toMeters(internalMm: number): number {
	return internalMm / MM_PER_METER;
}

function toMillimeters(internalM: number): number {
	return internalM * MM_PER_METER;
}

export function normalizeDistanceUnit(distanceUnit: unknown): DistanceUnit {
	if (
		distanceUnit === distanceUnits.METERS ||
		distanceUnit === distanceUnits.FEET ||
		distanceUnit === distanceUnits.US_SURVEY_FEET
	) {
		return distanceUnit;
	}
	return DEFAULT_SITE_UNITS.distance;
}

/** Input granularity and display precision, the same for every unit. */
const DISPLAY_DECIMALS = 2;
const DEFAULT_SPACING_CM = 30;
// 法面肩オフセットの UI 値は「内側」を正で表す (内部 mm は負のまま)
const DEFAULT_SLOPE_SHOULDER_OFFSET_CM = 50;
const DEFAULT_SPACING_FEET = 1;
const DEFAULT_SLOPE_SHOULDER_OFFSET_FEET = 1.5;

/** Resolves the UI display settings for a site distance unit. */
export function resolveSpreadingCompactionUnitConfig(
	distanceUnit: DistanceUnit,
): SpreadingCompactionUnitConfig {
	switch (distanceUnit) {
		case distanceUnits.FEET:
		case distanceUnits.US_SURVEY_FEET:
			return {
				distanceUnit,
				displayUnit: spreadingCompactionDisplayUnits.FEET,
				displayDecimals: DISPLAY_DECIMALS,
				defaultSpacingDisplay: DEFAULT_SPACING_FEET,
				defaultSlopeShoulderOffsetDisplay: DEFAULT_SLOPE_SHOULDER_OFFSET_FEET,
			};
		case distanceUnits.METERS:
		default:
			return {
				distanceUnit: distanceUnits.METERS,
				displayUnit: spreadingCompactionDisplayUnits.CENTIMETERS,
				displayDecimals: DISPLAY_DECIMALS,
				defaultSpacingDisplay: DEFAULT_SPACING_CM,
				defaultSlopeShoulderOffsetDisplay: DEFAULT_SLOPE_SHOULDER_OFFSET_CM,
			};
	}
}

/** Smallest settable value in the display unit. 0.01 for every unit. */
export function displayStep(
	unitConfig: Readonly<SpreadingCompactionUnitConfig>,
): number {
	return 10 ** -unitConfig.displayDecimals;
}

/** Snaps an input value to the display granularity. */
export function quantizeDisplayValue(
	displayValue: number,
	unitConfig: Readonly<SpreadingCompactionUnitConfig>,
): number {
	const base = 10 ** unitConfig.displayDecimals;
	return Math.round(displayValue * base) / base;
}

/**
 * Internal mm -> input field string, snapped to the display granularity.
 * Without this the raw conversion leaks float noise (305 mm -> "1.0006561679790025").
 */
export function formatDisplayInput(
	internalMm: number,
	unitConfig: Readonly<SpreadingCompactionUnitConfig>,
): string {
	return String(
		quantizeDisplayValue(
			convertMmToDisplay(internalMm, unitConfig),
			unitConfig,
		),
	);
}

/** Display unit -> internal mm, snapped to 1/1000 mm. */
export function convertDisplayToMm(
	displayValue: number,
	unitConfig: Readonly<SpreadingCompactionUnitConfig>,
): number {
	if (unitConfig.displayUnit === spreadingCompactionDisplayUnits.CENTIMETERS) {
		return roundMm(displayValue * MM_PER_CENTIMETER);
	}
	const internalM = createDistanceUnitPort(unitConfig.distanceUnit).toMeters(
		displayValue,
	);
	return roundMm(toMillimeters(internalM));
}

export function convertMmToDisplay(
	internalMm: number,
	unitConfig: Readonly<SpreadingCompactionUnitConfig>,
): number {
	const internalM = toMeters(internalMm);
	if (unitConfig.displayUnit === spreadingCompactionDisplayUnits.CENTIMETERS) {
		return internalM * CM_PER_METER;
	}
	return createDistanceUnitPort(unitConfig.distanceUnit).toDisplay(internalM);
}
